/**
 * Agent Chat Layout Component
 * Provides the complete layout structure for standalone agent chat interface with sidebar support
 */

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AgentChatSidebar } from './agent-chat-sidebar'
import { isBubbleMode } from '@/utils/bubble-mode'

/**
 * Props for the AgentChatLayout component
 * Accepts children to render within the main content area
 */
interface AgentChatLayoutProps {
  /**
   * Child components to render in the main content area
   * Typically the chat interface or conversation view
   */
  children: React.ReactNode
}

/**
 * Main layout component for the agent chat interface
 * Renders different layouts based on embed mode (bubble vs full sidebar)
 *
 * @param {AgentChatLayoutProps} props - Component props containing children
 * @returns {JSX.Element} The rendered layout with appropriate structure
 */
export function AgentChatLayout({ children }: AgentChatLayoutProps) {
  const bubbleMode = isBubbleMode()

  /**
   * Bubble mode layout - simplified view without sidebar
   * Used when chat is embedded as a bubble widget on external sites
   */
  if (bubbleMode) {
    return (
      <div className="h-dvh flex w-full overflow-hidden bg-background">
        <div className="flex flex-col flex-1 overflow-hidden">
          <main className="flex-1 overflow-hidden bg-background border border-border rounded-xl shadow-sm mx-1 mb-1">
            <div className="h-full overflow-hidden rounded-xl">
              {children}
            </div>
          </main>
        </div>
      </div>
    )
  }

  /**
   * Full layout with sidebar - standard view for agent chat
   * Includes collapsible sidebar with chat history and workspace info
   */
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="h-dvh flex w-full overflow-hidden bg-sidebar">
        <AgentChatSidebar className="w-[15rem] border-none" />

        <SidebarInset className="flex flex-col flex-1 overflow-hidden bg-sidebar">
          <main className="flex-1 overflow-hidden bg-background">
            <div className="h-full overflow-hidden rounded-xl">
              {children}
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
